/**
 * Pure state for the ask-user WIZARD (`AskUserWizard`) — the ratified
 * redesign of the answer surface (`lexide-ask-user-wizard-redesign-
 * miniplan.md`; contract: `lexide-ask-user-guide.md` §2–§3).
 *
 * IO-free so the step/pick/advance decision table is testable without a
 * DOM. The component maps returned state to rendering; it never
 * re-derives the rules.
 *
 * Semantics carried over from the Phase A card, deliberately:
 * - single-select re-tap CLEARS the pick (a mis-tap is recoverable
 *   without a reset control);
 * - answers collect as human-readable LABELS (the message enters a
 *   transcript a human reads), with the `Outro` free-text appended;
 * - submit needs ≥1 answered question; unanswered ones are OMITTED from
 *   the message, never sent as blanks (`formatAnswersMessage`'s
 *   unknown-stays-unknown rule).
 *
 * New under the ratified plan:
 * - one question per STEP; picking on a single-select step AUTO-ADVANCES
 *   (⚖ D-3) unless it is the last step;
 * - `Voltar` walks back with picks preserved;
 * - at most ONE `recommended` badge per question renders, whatever the
 *   wire says (the tool refuses two, but a wrong-looking recommendation
 *   on a lawyer-facing decision is worse than none — render defensively).
 *
 * Operator-ratified enhancements (2026-08-17, compare-to-Claude-Code pass):
 * - progress dots are CLICKABLE (`goToStep`) — jump to any question,
 *   picks preserved (jumping is navigation, never mutation);
 * - per-question ADAPTIVE layout (`questionLayout`): full-width ROWS when
 *   any choice carries a description, chips when none does — a described
 *   choice needs a full line to be read, an undescribed one reads faster
 *   as a chip.
 */

import type { AskUserAnswer, AskUserChoice, AskUserQuestion } from "@/lib/ask-user";

export interface WizardSelection {
  values: string[];
  other: string;
}

export interface WizardState {
  stepIndex: number;
  selections: Record<string, WizardSelection>;
}

export function initWizardState(questions: readonly AskUserQuestion[]): WizardState {
  const selections: Record<string, WizardSelection> = {};
  for (const q of questions) selections[q.id] = { values: [], other: "" };
  return { stepIndex: 0, selections };
}

function selectionOf(state: WizardState, id: string): WizardSelection {
  return state.selections[id] ?? { values: [], other: "" };
}

/**
 * Apply a chip pick. Returns the next state and whether the wizard
 * AUTO-ADVANCED (⚖ D-3): single-select, pick (not clear), not the last
 * step. Multi-select toggles and never advances; the explicit button does.
 */
export function pickChoice(
  state: WizardState,
  questions: readonly AskUserQuestion[],
  question: AskUserQuestion,
  value: string,
): { state: WizardState; advanced: boolean } {
  const cur = selectionOf(state, question.id);
  const has = cur.values.includes(value);
  const values = question.multiSelect
    ? has
      ? cur.values.filter((v) => v !== value)
      : [...cur.values, value]
    : has
      ? []
      : [value];
  const next: WizardState = {
    ...state,
    selections: { ...state.selections, [question.id]: { ...cur, values } },
  };
  const advanced =
    !question.multiSelect && !has && state.stepIndex < questions.length - 1;
  return { state: advanced ? { ...next, stepIndex: next.stepIndex + 1 } : next, advanced };
}

export function setOtherText(
  state: WizardState,
  questionId: string,
  other: string,
): WizardState {
  const cur = selectionOf(state, questionId);
  return { ...state, selections: { ...state.selections, [questionId]: { ...cur, other } } };
}

export function goBack(state: WizardState): WizardState {
  return state.stepIndex === 0 ? state : { ...state, stepIndex: state.stepIndex - 1 };
}

export function goForward(state: WizardState, total: number): WizardState {
  return state.stepIndex >= total - 1 ? state : { ...state, stepIndex: state.stepIndex + 1 };
}

/** Jump to an arbitrary step (clickable dots). Out-of-range is a no-op —
 *  never a clamp, so a stale index cannot silently land on the wrong
 *  question. Picks are untouched: jumping is navigation, not mutation. */
export function goToStep(state: WizardState, index: number, total: number): WizardState {
  if (!Number.isInteger(index) || index < 0 || index >= total) return state;
  return index === state.stepIndex ? state : { ...state, stepIndex: index };
}

/**
 * Adaptive layout per question: "rows" when ANY choice carries a
 * description (a described choice needs a full-width line to be read),
 * "chips" when none does.
 *
 * ⚠ Blank-guarded on purpose: empty string is how this wire spells
 * "absent" (`??` would not catch it) — a `description: ""` must not force
 * every choice onto its own row.
 */
export function questionLayout(question: AskUserQuestion): "rows" | "chips" {
  return question.choices?.some((c) => Boolean(c.description && c.description.trim()))
    ? "rows"
    : "chips";
}

/**
 * A dot's tooltip/aria-label: the question's `topic` (the ≤16-char label
 * the tool ships per the mission-ingest handshake, `5eeffd836`) with the
 * position kept for orientation; numeric-only when absent. Blank-guarded
 * here too even though the parser already absents whitespace — render
 * defensively, same posture as `recommendedChoice`.
 */
export function dotLabel(
  question: AskUserQuestion,
  index: number,
  total: number,
): string {
  const topic = question.topic?.trim();
  return topic
    ? `${topic} — pergunta ${index + 1} de ${total}`
    : `Pergunta ${index + 1} de ${total}`;
}

/** Labels (not machine values) + trimmed `Outro`, per the card's contract. */
export function collectAnswers(
  state: WizardState,
  questions: readonly AskUserQuestion[],
): AskUserAnswer[] {
  return questions.map((q) => {
    const cur = selectionOf(state, q.id);
    const labels = cur.values.map(
      (v) => q.choices?.find((c) => c.value === v)?.label ?? v,
    );
    const other = cur.other.trim();
    return { id: q.id, prompt: q.prompt, values: other ? [...labels, other] : labels };
  });
}

export function answeredCount(
  state: WizardState,
  questions: readonly AskUserQuestion[],
): number {
  return collectAnswers(state, questions).filter((a) => a.values.length > 0).length;
}

/**
 * The choice that renders the ⭐ badge for a question, or undefined.
 *
 * ⚠ FIRST `recommended` only, by decision: the tool refuses two, so a
 * wire carrying two has bypassed validation — rendering both would show
 * the lawyer a contradiction ("two defaults") on a decision surface.
 * One badge from tainted data is still meaningful; two are noise.
 */
export function recommendedChoice(
  question: AskUserQuestion,
): AskUserChoice | undefined {
  return question.choices?.find((c) => c.recommended === true);
}
